import type { TFunction } from "i18next";
import { DateTime } from "luxon";

import type {
  CertificationDetail,
  CertificationType,
  ContentItemOutline,
  ModuleOutline,
  Track,
} from "@/redux/certifications/types";
import {
  LiveSession,
  ModuleVideoSummary,
  PathCopy,
} from "@/domains/certifications/types";
import { CONTENT_ITEM_TYPES } from "@/domains/certifications/constants";

const CONTENT_KEYS: Record<CertificationType, string> = {
  WELL_BEING: "wellBeing",
};

export const getVideoDetails = (item: ContentItemOutline) =>
  item.type === CONTENT_ITEM_TYPES.VIDEO ? item.details : null;

export const getResourceDetails = (item: ContentItemOutline) =>
  item.type === CONTENT_ITEM_TYPES.RESOURCE ? item.details : null;

export const getLiveSessionDetails = (item: ContentItemOutline) =>
  item.type === CONTENT_ITEM_TYPES.LIVE_SESSION ? item.details : null;

export const getUpcomingLiveSessions = (
  certification: CertificationDetail,
  now: DateTime = DateTime.now(),
): LiveSession[] =>
  certification.modules
    .flatMap((module) =>
      module.items.flatMap((item) => {
        const details = getLiveSessionDetails(item);
        return details && DateTime.fromISO(details.scheduledEnd) >= now
          ? [
              {
                id: item.id,
                title: item.title,
                scheduledStart: details.scheduledStart,
                joinUrl: details.joinUrl,
                presenter: null,
              },
            ]
          : [];
      }),
    )
    .sort(
      (a, b) =>
        DateTime.fromISO(a.scheduledStart).toMillis() -
        DateTime.fromISO(b.scheduledStart).toMillis(),
    );

export const toBackendUrl = (path: string) =>
  new URL(
    path,
    (import.meta.env.VITE_APP_BACKEND_URL as string | undefined) ||
      window.location.origin,
  ).href;

export const getContentKey = (type: CertificationType) =>
  `certification.${CONTENT_KEYS[type]}`;

export const getModuleVideoSummary = (
  module: ModuleOutline,
): ModuleVideoSummary => {
  const videos = module.items.filter(
    (item) => item.type === CONTENT_ITEM_TYPES.VIDEO,
  );
  const totalSeconds = videos.reduce(
    (sum, video) => sum + (getVideoDetails(video)?.durationSeconds ?? 0),
    0,
  );

  return {
    videoCount: videos.length,
    totalMinutes: Math.round(totalSeconds / 60),
  };
};

export const getModuleVideoMeta = (t: TFunction, module: ModuleOutline) => {
  const { videoCount, totalMinutes } = getModuleVideoSummary(module);
  return t("certification.labels.moduleMeta", {
    videos: t("certification.labels.videoCount", { count: videoCount }),
    minutes: totalMinutes,
  });
};

const fallbackPathCopy = (track: Track): PathCopy => ({
  eyebrow: "",
  title: track.title,
  shortTitle: track.title,
  summary: "",
  requirements: [],
  requirementsNote: "",
  requirementSentence: "",
  timePerWeek: "",
  credential: track.title,
  represents: "",
  suits: "",
  switchAhead: "",
  switchExtraWork: "",
  helpQuestion: "",
  helpLink: "",
});

export const getPathCopy = (
  t: TFunction,
  contentKey: string,
  track: Track,
): PathCopy => {
  const copy = t(`${contentKey}.paths.${track.slug}`, { returnObjects: true });
  const translated =
    typeof copy === "object" && copy !== null
      ? (copy as Partial<PathCopy>)
      : {};
  return { ...fallbackPathCopy(track), ...translated };
};

export const getPathShortTitle = (
  t: TFunction,
  certification: Pick<CertificationDetail, "type" | "tracks">,
  trackId: number,
) => {
  const track = certification.tracks.find((item) => item.id === trackId);
  return track
    ? getPathCopy(t, getContentKey(certification.type), track).shortTitle
    : null;
};

const NON_TEXT_SELECTOR = "script, style, template, noscript";

export const htmlToText = (html: string) => {
  const { body } = new DOMParser().parseFromString(html, "text/html");
  body
    .querySelectorAll(NON_TEXT_SELECTOR)
    .forEach((element) => element.remove());
  const blocks = Array.from(body.children, (element) =>
    (element.textContent ?? "").trim(),
  ).filter(Boolean);
  return blocks.length > 0 ? blocks.join(" ") : (body.textContent ?? "").trim();
};

const DATE_FORMAT = "dd MMMM yyyy";
const SHORT_DATE_FORMAT = "d MMMM";

export const formatCertificationDate = (iso: string) =>
  DateTime.fromISO(iso).toFormat(DATE_FORMAT);

export const formatShortCertificationDate = (iso: string) =>
  DateTime.fromISO(iso).toFormat(SHORT_DATE_FORMAT);

export const getInitials = (fullName: string) =>
  fullName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const isForbiddenWithCode = (error: unknown, code: string) =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  error.status === 403 &&
  "data" in error &&
  typeof error.data === "object" &&
  error.data !== null &&
  "code" in error.data &&
  error.data.code === code;

export const isNotEnrolledError = (error: unknown) =>
  isForbiddenWithCode(error, "not_enrolled");

export const isRemovedFromCertificationError = (error: unknown) =>
  isForbiddenWithCode(error, "removed_from_certification");

export const getWeekNumber = (modules: ModuleOutline[], moduleId: number) => {
  const index = [...modules]
    .sort((a, b) => a.order - b.order)
    .findIndex(({ id }) => id === moduleId);
  return index === -1 ? null : index + 1;
};

export const getWeekByTopic = (modules: ModuleOutline[]) =>
  new Map(
    [...modules]
      .sort((a, b) => a.order - b.order)
      .flatMap((module, index) =>
        module.topic ? [[module.topic.id, index + 1] as const] : [],
      ),
  );
